import {put, call} from 'redux-saga/effects';
import AccountingConnection from "../../entities/accountingConnection";
import * as Actions from "../actions/accountingConnectionActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type AccountingConnectionAction = {
    accountingConnectionId: string;
    accountingConnection: AccountingConnection;
    accountingConnections: AccountingConnection[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneAccountingConnection(action: AccountingConnectionAction) {
	const session = tabSession.captureGeneration();
	try {
		const accountingConnection: AccountingConnection = yield call(api.findOne, 'accountingConnection', action.accountingConnectionId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ACCOUNTINGCONNECTION_FETCH_ONE_SUCCESS, accountingConnectionId: action.accountingConnectionId, accountingConnection});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACCOUNTINGCONNECTION_FETCH_ONE_FAILURE, accountingConnectionId: action.accountingConnectionId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {AccountingConnectionAction} action
 */
export function* saveAccountingConnection(action: AccountingConnectionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE saga', action.accountingConnection?.id);
    
	if (action.accountingConnection === undefined || action.accountingConnection === null) {
		console.error('saveAccountingConnection received a null');
		throw new Error('saveAccountingConnection received a null');
	}

	if (!(action.accountingConnection instanceof AccountingConnection)) {
		action.accountingConnection = new AccountingConnection(action.accountingConnection);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'accountingConnection', action.accountingConnection);
		if (addResult.isErr()) {
			console.error(`⮂ saveAccountingConnection: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.ACCOUNTINGCONNECTION_SAVE_FAILURE, accountingConnection: action.accountingConnection, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ACCOUNTINGCONNECTION_SAVE, accountingConnection: action.accountingConnection});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.ACCOUNTINGCONNECTION_SAVE_FAILURE, accountingConnection: action.accountingConnection, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {AccountingConnectionAction} action
 * @param {AccountingConnection[]} action.accountingConnections
 */
export function* saveManyAccountingConnections(action: AccountingConnectionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_MANY saga', action.accountingConnections.map(accountingConnection => accountingConnection.id).join(','));
    
    if (!Array.isArray(action.accountingConnections)) {
        console.error('saveManyAccountingConnections expected an array, received', action.accountingConnections);
        throw new Error('saveManyAccountingConnections expected an array');
    }

	try {
		const toSave: AccountingConnection[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.accountingConnections.length; i++) {
			const accountingConnection = action.accountingConnections[i];
			accountingConnection.persisted = false;
			toSave.push(accountingConnection);
		}

		const addResult = yield call(idb.add, 'accountingConnection', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyAccountingConnections: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.ACCOUNTINGCONNECTION_SAVE_MANY_FAILURE, accountingConnections: action.accountingConnections, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ACCOUNTINGCONNECTION_SAVE_MANY, accountingConnections: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.ACCOUNTINGCONNECTION_SAVE_MANY_FAILURE, accountingConnections: action.accountingConnections, status: 500, err});

		console.error(`Error saving ${action.accountingConnections.length} accountingConnections to database`, err);
	}
}

/**
 * @param {AccountingConnectionAction} action
 */
export function* removeAccountingConnection(action: AccountingConnectionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.accountingConnection === undefined || action.accountingConnection === null || !(action.accountingConnection instanceof AccountingConnection)) {
			console.error('removeAccountingConnection expected type AccountingConnection, received', action.accountingConnection);
			throw new Error('removeAccountingConnection expected type AccountingConnection');
		}

	try {
		// Mark as deleted
		action.accountingConnection.status = 'D';
		action.accountingConnection.addedOn = newHRTimestamp();
		action.accountingConnection.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'accountingConnection', action.accountingConnection);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeAccountingConnection: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACCOUNTINGCONNECTION_REMOVE_FAILURE, accountingConnection: action.accountingConnection, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACCOUNTINGCONNECTION_REMOVE, accountingConnection: action.accountingConnection});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACCOUNTINGCONNECTION_REMOVE_FAILURE, accountingConnection: action.accountingConnection, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this AccountingConnection.
	*
	* @param {AccountingConnectionAction} action
	*/
export function* restoreAccountingConnection(action: AccountingConnectionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreAccountingConnection expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.accountingConnection === undefined || action.accountingConnection === null) {
        console.error('restoreAccountingConnection expected a AccountingConnection, received null');
        throw new Error('restoreAccountingConnection expected a AccountingConnection, received null');
    }
    else if (!(action.accountingConnection instanceof AccountingConnection)) action.accountingConnection = new AccountingConnection(action.accountingConnection); 
    
	try {
		// Mark as restored
		action.accountingConnection.status = 'U';
		action.accountingConnection.addedOn = newHRTimestamp();
		action.accountingConnection.addedBy = userId;
		action.accountingConnection.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'accountingConnection', action.accountingConnection);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreAccountingConnection: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACCOUNTINGCONNECTION_RESTORE_FAILURE, accountingConnection: action.accountingConnection, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACCOUNTINGCONNECTION_RESTORE, accountingConnection: action.accountingConnection});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACCOUNTINGCONNECTION_RESTORE_FAILURE, accountingConnection: action.accountingConnection, status: 500, err});

		console.error('Error restoring from database', err);
	}
}